import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const apiData = {
  hero: {
    badge: "API Development",
    headline: "Secure & Scalable API Development",
    subtext: "We design and build REST APIs that power web applications, mobile apps and third-party integrations with security and performance at the core.",
    primaryCTA: "Build Your API",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "REST API Development", desc: "Well-structured REST APIs following industry standards with versioning and documentation.", icon: "ri-exchange-line" },
    { title: "Laravel APIs", desc: "APIs built with Laravel Sanctum, Passport and best practices for authentication.", icon: "ri-code-s-slash-line" },
    { title: "Mobile APIs", desc: "Optimized APIs for mobile consumption with caching, pagination and offline support.", icon: "ri-smartphone-line" },
    { title: "Third-party Integrations", desc: "Connect with payment gateways, CRMs, ERPs and external service providers.", icon: "ri-links-line" },
    { title: "Payment APIs", desc: "Secure payment processing with Stripe, PayPal and custom gateway integrations.", icon: "ri-bank-card-line" },
    { title: "Authentication", desc: "OAuth 2.0, JWT, SAML and multi-factor authentication implementation.", icon: "ri-shield-check-line" },
    { title: "API Security", desc: "Rate limiting, input validation, CORS, encryption and penetration testing.", icon: "ri-lock-2-line" },
    { title: "API Performance", desc: "Caching strategies, query optimization and load balancing for high throughput.", icon: "ri-speed-line" },
  ],
  architecture: {
    layers: [
      { name: "Client Applications", icon: "ri-computer-line", desc: "Web / Mobile / Third-party" },
      { name: "API Gateway", icon: "ri-shield-line", desc: "Rate Limit / Auth / Routing" },
      { name: "Laravel API", icon: "ri-code-s-slash-line", desc: "Controllers / Middleware / Validation" },
      { name: "Service Layer", icon: "ri-stack-line", desc: "Business Logic / Jobs / Events" },
      { name: "Database", icon: "ri-database-2-line", desc: "MySQL / PostgreSQL / MongoDB" },
      { name: "Cache & Queue", icon: "ri-server-line", desc: "Redis / RabbitMQ / SQS" },
      { name: "External APIs", icon: "ri-cloud-line", desc: "Payment / SMS / AI / Analytics" },
    ],
  },
  faq: [
    { q: "What API standards do you follow?", a: "We follow RESTful principles, OpenAPI 3.0 for documentation, semantic versioning and JSON:API standards where appropriate." },
    { q: "How do you secure APIs?", a: "We implement OAuth 2.0 / JWT authentication, rate limiting, input validation, HTTPS-only, CORS policies and regular security audits." },
    { q: "Can you integrate with our existing systems?", a: "Yes. We build APIs that integrate with CRMs, ERPs, payment gateways, logistics providers and any system with available endpoints." },
    { q: "Do you provide API documentation?", a: "Yes. We provide comprehensive Swagger/OpenAPI documentation with code examples, authentication guides and SDK recommendations." },
  ],
};

export default function ApiDevelopmentPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "API Development" },
        ]}
      />

      <PageHero
        badge={apiData.hero.badge}
        headline={apiData.hero.headline}
        subtext={apiData.hero.subtext}
        primaryCTA={apiData.hero.primaryCTA}
        secondaryCTA={apiData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">API Development Services</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {apiData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">API Architecture</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center gap-3">
              {apiData.architecture.layers.map((layer, i) => (
                <AnimatedSection key={layer.name} delay={i * 120}>
                  <div className="relative">
                    {i > 0 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-gradient-to-b from-transparent to-accent-500/40" />}
                    <div className="flex items-center gap-4 px-6 py-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 w-[280px] md:w-[360px]">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600">
                        <i className={`${layer.icon} text-lg`} />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950 font-heading">{layer.name}</h4>
                        <p className="text-xs text-foreground-500">{layer.desc}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQSection heading="API Development FAQ" items={apiData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}